import { Injectable, inject } from '@angular/core';
import { forkJoin, map } from 'rxjs';
import { ApiResponse } from '../models/api-response.models';
import { unwrapApiResponse } from '../utils/api-response';
import { ApiService } from './api.service';

export interface ExpenseType {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
}
export interface Expense {
  id: string;
  expenseTypeId: string;
  expenseTypeName: string;
  expenseDate: string;
  amount: number;
  description: string;
  referenceNumber?: string;
  paymentMethod?: string;
}
export interface FinanceSummary {
  year: number;
  month: number;
  daysElapsed: number;
  daysInMonth: number;
  monthlyRevenue: number;
  monthlyExpenses: number;
  monthlyPurchases: number;
  monthlyNet: number;
  yearlyRevenue: number;
  yearlyExpenses: number;
  yearlyPurchases: number;
  yearlyNet: number;
  totalBudget: number;
  budgetRemaining: number;
  outstandingReceivable: number;
  outstandingPayable: number;
  categories: Array<{
    expenseTypeId: string;
    name: string;
    budget: number;
    spent: number;
    remaining: number;
  }>;
}
export interface DebtAccount {
  id: string; direction: 'Receivable' | 'Payable'; partyName: string; phone?: string; email?: string;
  description?: string; referenceNumber?: string; originalAmount: number; outstandingAmount: number;
  paidAmount: number; debtDate: string; dueDate?: string; status: string; isOverdue: boolean;
  isInvestment: boolean; storeId?: string; agreementUrl?: string; agreementFileName?: string; profitMode: string; profitRate: number;
  agreementProfitAmount: number; profitPaidAmount: number; profitOutstanding: number; totalOutstanding: number; allocatedAmount: number;
  realisedProfit?: number; realisedRevenue?: number; availablePrincipal?: number; retainedProfit?: number; availableFund?: number; capitalReleased?: number;
}
export interface DebtWorkspace { items: DebtAccount[]; summary: { receivable: number; payable: number; net: number; openCount: number }; }
export interface FranchiseMonthlyProfit { partnerId: string; partnerName: string; month: string; monthLabel: string; periodReference: string; realisedProfit: number; realisedRevenue: number; accruedProfit: number; periodPaid: number; amountToPay: number; totalProfitOutstanding: number; }
export interface CompanyFundWorkspace {
  totalBalance: number;
  accounts: Array<{ id: string; storeId: string; balance: number; updatedAt: string }>;
  transactions: Array<{ id: string; companyFundAccountId: string; purchaseId?: string; type: string; amount: number; balanceAfter: number; referenceNumber?: string; notes?: string; transactionDate: string }>;
}

@Injectable({ providedIn: 'root' })
export class FinanceService {
  private readonly api = inject(ApiService);
  expenseTypes() {
    return this.api
      .get<ApiResponse<ExpenseType[]> | ExpenseType[]>(
        '/finance/expense-types',
        undefined,
        'marketplace',
      )
      .pipe(map(unwrapApiResponse));
  }
  createExpenseType(body: { name: string; description?: string }) {
    return this.api
      .post<ApiResponse<ExpenseType> | ExpenseType, typeof body>(
        '/finance/expense-types',
        body,
        'marketplace',
      )
      .pipe(map(unwrapApiResponse));
  }
  updateExpenseType(id: string, body: { name: string; description?: string; isActive: boolean }) {
    return this.api
      .put<ApiResponse<ExpenseType> | ExpenseType, typeof body>(
        `/finance/expense-types/${id}`,
        body,
        'marketplace',
      )
      .pipe(map(unwrapApiResponse));
  }
  expenses(from: string, to: string) {
    return this.api
      .get<ApiResponse<Expense[]> | Expense[]>('/finance/expenses', { from, to }, 'marketplace')
      .pipe(map(unwrapApiResponse));
  }
  createExpense(body: Omit<Expense, 'id' | 'expenseTypeName'>) {
    return this.api
      .post<ApiResponse<Expense> | Expense, typeof body>('/finance/expenses', body, 'marketplace')
      .pipe(map(unwrapApiResponse));
  }
  deleteExpense(id: string) {
    return this.api.delete(`/finance/expenses/${id}`, 'marketplace');
  }
  saveBudget(body: { expenseTypeId: string; year: number; month: number; amount: number }) {
    return this.api.put(`/finance/budgets`, body, 'marketplace');
  }
  companyFund(storeId?: string) {
    return this.api.get<ApiResponse<CompanyFundWorkspace> | CompanyFundWorkspace>('/finance/company-fund', storeId ? { storeId } : undefined, 'marketplace').pipe(map(unwrapApiResponse));
  }
  recordCompanyFund(body: { storeId: string; amount: number; type: 'Deposit' | 'Withdrawal'; referenceNumber?: string; notes?: string; transactionDate?: string }) {
    return this.api.post('/finance/company-fund/transactions', body, 'marketplace');
  }
  summary(year: number, month: number) {
    return this.api
      .get<ApiResponse<FinanceSummary> | FinanceSummary>(
        '/finance/summary',
        { year, month },
        'marketplace',
      )
      .pipe(map(unwrapApiResponse));
  }
  debts(status = 'all') {
    return this.api.get<ApiResponse<DebtWorkspace> | DebtWorkspace>('/finance/debts', { status }, 'marketplace').pipe(map(unwrapApiResponse));
  }
  recordDebtPayment(id: string, body: { amount: number; paymentDate: string; paymentMethod: string; referenceNumber?: string; notes?: string; recordAsExpense: boolean; expenseTypeId?: string; component?: string }) {
    return this.api.post<ApiResponse<any> | any, typeof body>(`/finance/debts/${id}/payments`, body, 'marketplace').pipe(map(unwrapApiResponse));
  }
  createInvestment(body: FormData) { return this.api.post<ApiResponse<DebtAccount>|DebtAccount,FormData>('/finance/investments',body,'marketplace',60_000).pipe(map(unwrapApiResponse)); }
  franchisePartners(storeId?:string) { return this.api.get<ApiResponse<any[]>|any[]>('/finance/franchise-partners',storeId?{storeId}:undefined,'marketplace').pipe(map(unwrapApiResponse)); }
  myFranchiseDashboard() { return this.api.get<ApiResponse<any>|any>('/finance/franchise/me',undefined,'marketplace').pipe(map(unwrapApiResponse)); }
  previousMonthFranchiseProfit(id:string) { return this.api.get<ApiResponse<FranchiseMonthlyProfit>|FranchiseMonthlyProfit>(`/finance/franchise-partners/${id}/previous-month-profit`,undefined,'marketplace').pipe(map(unwrapApiResponse)); }
  investmentProducts() { return this.api.get<ApiResponse<any[]>|any[]>('/finance/investment-products',undefined,'marketplace').pipe(map(unwrapApiResponse)); }
  allocateInvestment(id:string, body:{purchaseItemId:string;amount:number;notes?:string}) { return this.api.post(`/finance/debts/${id}/allocations`,body,'marketplace'); }
  workspace(year: number, month: number, from: string, to: string) {
    return forkJoin({
      types: this.expenseTypes(),
      expenses: this.expenses(from, to),
      summary: this.summary(year, month),
      debts: this.debts(),
      companyFund: this.companyFund(),
    });
  }
}
